/**
 * Redux slice for session management
 * Handles session list, current session, notes, and session operations
 *
 * State is split into two separate session lists:
 * - ownerSessions: Current user's own sessions (safe to delete)
 * - allSessions: All sessions across all users (read-only view for admins)
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { sessionService } from '../services/sessionService';
import { LOADING_STATE } from '../types/constants';
import type { FieldValue } from '../utils/fieldUtils';
import { EDIT_SOURCES, MERGE_SELECTION_SIZE } from '../constants';

// ===== TYPES =====

// 'archived' marks a source that was merged into another session: read-only for everyone.
export type SessionStatus = 'recording' | 'paused' | 'archived';

export interface SessionNote {
  note_id: string;
  session_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  content: string;
}

/** edited_by prefix for a reviewer's validation; matches the service's EDIT_SOURCE_USER_VALIDATION. */
export const EDIT_SOURCE_USER_VALIDATION = EDIT_SOURCES.USER_VALIDATION;

/** Whether an edit-history entry records a validation review rather than a value change. */
export const isValidationEdit = (entry: { edited_by: string }): boolean =>
  entry.edited_by.startsWith(`${EDIT_SOURCE_USER_VALIDATION}#`);

// One net change to a field, keyed by (edited_by, field_name). edited_by is a
// composite "<PREFIX>#<ref>" (e.g. USER_VALIDATION#alias); see backend EditHistoryEntry.
export interface EditHistoryEntry {
  timestamp: string;
  edited_by: string;
  field_name: string;
  old_value?: any;
  new_value?: any;
  comment?: string;  // optional free-text note (e.g. why a validator changed a value)
}

// Server-computed validation lifecycle. Absent/empty = not started.
export type ValidationState = 'validating' | 'validated';

// Audit lifecycle — a separate axis from ValidationState, so an audit starting never affects
// anything gated on validation_state === 'validated'. Absent = not audited yet.
export type AuditState = 'auditing' | 'audited';

/** An auditor's call on one validated field, comparing the user's answer to the LLM's. */
export type AuditResult = 'user_correct' | 'llm_correct' | 'both_correct' | 'neither_correct';

/** What an auditor submits for one field; the server supplies the rest of AuditedField. */
export interface AuditFieldInput {
  result: AuditResult;
  confirmed_value?: FieldValue;  // required for 'neither_correct'
  comment?: string;
}

/** A stored result — a measurement, never part of the session's displayed values. One per
 *  field: re-auditing replaces it, so audited_by/audited_at name the call that stands. */
export interface AuditedField extends AuditFieldInput {
  llm_value?: FieldValue;
  user_value?: FieldValue;
  audited_by: string;
  audited_at: string;
}

export interface SessionListDTO {
  session_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  status: SessionStatus;
  use_case_type: string;
  audio_count: number;
  total_duration_seconds: number;
  total_word_count: number;
  language_code: string;
  total_speakers: number;
  group_id?: string | null;
  metadata?: Record<string, any> | null;
  validation_state?: ValidationState | null;
  audit_state?: AuditState | null;
  // Server-computed reasons this session can't be picked for a merge (empty = selectable).
  merge_blockers?: string[];
}

export interface Session extends SessionListDTO {
  full_text: string;
  running_tasks: string[];  // Active background tasks (e.g., ["analyzing", "postprocessing"])
  summary_content: string | null;
  summary_generated_at: string | null;
  extracted_fields: Record<string, string | string[] | Record<string, string[]>>;
  fields_last_updated?: string | null;
  human_overrides_fields: Record<string, string | string[] | Record<string, string[]>>;
  // Final field values captured from the AUSTIN form at submit time (autofill plugin).
  submitted_fields?: Record<string, string | string[] | Record<string, string[]>>;
  notes?: SessionNote[] | null;
  notes_evidence?: Record<string, string[]>;  // {field_name: [quote1, quote2, ...]}
  // Per-(edited_by, field) change log; validation progress is derived from USER_VALIDATION#* entries.
  edit_history?: EditHistoryEntry[];
  // Audit result per field. Deliberately outside the display precedence (see
  // mergeWithHumanOverrides), edit_history, and the AUSTIN backfill: results measure
  // accuracy, they do not change the session.
  audited_fields?: Record<string, AuditedField>;
}

/** Whether merge mode is on, and the sessions picked for a merge in the order they were picked. */
export interface SessionMergeSelectionState {
  isMergeMode: boolean;
  selectedIds: string[];
  /** In merge mode, hide sessions whose own blockers already rule them out. */
  hideUnmergeable: boolean;
}

interface SessionState {
  // User's own sessions (from GET /api/sessions)
  ownerSessions: SessionListDTO[];
  ownerSessionsStatus: LOADING_STATE;

  // All sessions across all users (from GET /api/sessions/all) — admin read-only view
  allSessions: SessionListDTO[];
  allSessionsStatus: LOADING_STATE;
  allSessionsNextToken: string | null;
  allSessionsLoading: boolean;  // true while progressively loading pages
  // "Show All Sessions" toggle — persisted here so it survives navigation away
  // from the sessions list and back (would otherwise reset with local state).
  viewAllSessions: boolean;

  // An auditor's work list (from GET /api/sessions/validated) — spans all users and sites, so
  // kept separate from ownerSessions/allSessions rather than merged. Each row carries
  // audit_state, so the review-state filter shows what is audited vs still waiting.
  validatedSessions: SessionListDTO[];
  validatedSessionsStatus: LOADING_STATE;
  // null = the user hasn't chosen, so the default applies (on for auditors — it's their work
  // list). Once they toggle it, their choice is a real boolean and survives navigation.
  viewValidatedSessions: boolean | null;

  // Admin "Show Archived" toggle: list requests ask the server to include archived sessions
  // (merged sources), which the default lists hide.
  includeArchivedSessions: boolean;

  // Admin merge mode on the sessions list and the sessions picked for a merge. Kept here so
  // both survive a visit to the compare page and back.
  mergeSelection: SessionMergeSelectionState;

  // Current session detail + notes
  currentSession: Session | null;
  notes: SessionNote[];
  error: string | null;

  fetchSessionStatus: LOADING_STATE;
  updateFieldsStatus: LOADING_STATE;
  generateSummaryStatus: LOADING_STATE;
  saveNoteStatus: LOADING_STATE;
  updateMetadataStatus: LOADING_STATE;
  reanalysisStatus: 'idle' | 'pending' | 'fulfilled';

  // True while useSessionUpdates is actively polling for background-processing
  // updates. Read by navigation handlers to decide whether a client-side
  // navigation is safe or must fall back to a hard page load (prevents the
  // in-flight poll from repopulating currentSession and bouncing the user back).
  isPolling: boolean;
}

// ===== INITIAL STATE =====

const initialState: SessionState = {
  ownerSessions: [],
  ownerSessionsStatus: 'idle',
  allSessions: [],
  allSessionsStatus: 'idle',
  allSessionsNextToken: null,
  allSessionsLoading: false,
  viewAllSessions: false,
  validatedSessions: [],
  validatedSessionsStatus: 'idle',
  viewValidatedSessions: null,
  includeArchivedSessions: false,
  mergeSelection: { isMergeMode: false, selectedIds: [], hideUnmergeable: false },
  currentSession: null,
  notes: [],
  error: null,
  fetchSessionStatus: 'idle',
  updateFieldsStatus: 'idle',
  generateSummaryStatus: 'idle',
  saveNoteStatus: 'idle',
  updateMetadataStatus: 'idle',
  reanalysisStatus: 'idle',
  isPolling: false,
};

// ===== ASYNC THUNKS =====

/** Fetch the current user's own sessions */
export const fetchSessions = createAsyncThunk(
  'session/fetchSessions',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { includeArchivedSessions } = (getState() as { session: SessionState }).session;
      const sessions = await sessionService.fetchSessions(includeArchivedSessions);
      return sessions;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch sessions');
    }
  }
);

/**
 * Fetch all sessions with cursor-based pagination (admin/engineer only).
 * Pass null/undefined for first page (resets list), or a nextToken to append next page.
 */
export const fetchAllSessions = createAsyncThunk(
  'session/fetchAllSessions',
  async (nextToken: string | null | undefined, { getState, rejectWithValue }) => {
    try {
      const { includeArchivedSessions } = (getState() as { session: SessionState }).session;
      const result = await sessionService.fetchAllSessions(nextToken ?? null, includeArchivedSessions);
      return { ...result, isFirstPage: !nextToken };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch all sessions');
    }
  }
);

export const fetchSession = createAsyncThunk(
  'session/fetchSession',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const session = await sessionService.fetchSession(sessionId);
      return session;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch session');
    }
  }
);

export const updateSessionFields = createAsyncThunk(
  'session/updateFields',
  async (
    { sessionId, fields }: { sessionId: string; fields: Record<string, string | string[] | Record<string, string[]>> },
    { rejectWithValue }
  ) => {
    try {
      const updatedSession = await sessionService.updateFields(sessionId, fields);
      return updatedSession;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update fields');
    }
  }
);

export const validateSessionFields = createAsyncThunk(
  'session/validateFields',
  async (
    {
      sessionId,
      fields,
      comments,
    }: {
      sessionId: string;
      fields: Record<string, string | string[] | Record<string, string[]>>;
      comments?: Record<string, string>;
    },
    { rejectWithValue }
  ) => {
    try {
      const updatedSession = await sessionService.validateFields(sessionId, fields, comments);
      return updatedSession;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to validate fields');
    }
  }
);

export const fetchValidatedSessions = createAsyncThunk(
  'session/fetchValidatedSessions',
  async (_, { rejectWithValue }) => {
    try {
      return await sessionService.fetchValidatedSessions();
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch validated sessions');
    }
  }
);

export const auditSessionFields = createAsyncThunk(
  'session/auditFields',
  async (
    { sessionId, fields }: { sessionId: string; fields: Record<string, AuditFieldInput> },
    { rejectWithValue }
  ) => {
    try {
      return await sessionService.auditFields(sessionId, fields);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || error.message || 'Failed to save audit'
      );
    }
  }
);

export const updateSessionGroupId = createAsyncThunk(
  'session/updateGroupId',
  async (
    { sessionId, groupId }: { sessionId: string; groupId: string },
    { rejectWithValue }
  ) => {
    try {
      const result = await sessionService.updateGroupId(sessionId, groupId);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update site');
    }
  }
);

export const generateSummary = createAsyncThunk(
  'session/generateSummary',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const result = await sessionService.generateSummary(sessionId);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to generate summary');
    }
  }
);

export const saveNote = createAsyncThunk(
  'session/saveNote',
  async (
    { sessionId, content, noteId }: { sessionId: string; content: string; noteId?: string },
    { rejectWithValue }
  ) => {
    try {
      const { note, reanalysis_triggered } = await sessionService.saveSessionNote(sessionId, content, noteId);
      return { note, isUpdate: Boolean(noteId), reanalysisTriggered: reanalysis_triggered };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to save note');
    }
  }
);

export const fetchSessionStatusThunk = createAsyncThunk(
  'session/fetchSessionStatus',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const result = await sessionService.fetchSessionStatus(sessionId);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch session status');
    }
  }
);

export const deleteSession = createAsyncThunk(
  'session/deleteSession',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      await sessionService.deleteSession(sessionId);
      return sessionId;
    } catch (error: any) {
      const detail = error.response?.data?.detail || error.message || 'Failed to delete session';
      return rejectWithValue(detail);
    }
  }
);

export const updateSessionMetadata = createAsyncThunk(
  'session/updateMetadata',
  async (
    { sessionId, metadata }: { sessionId: string; metadata: Record<string, any> },
    { rejectWithValue }
  ) => {
    try {
      const result = await sessionService.updateMetadata(sessionId, metadata);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update metadata');
    }
  }
);


// ===== SLICE =====

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    clearCurrentSession: (state) => {
      state.currentSession = null;
      state.notes = [];
      state.error = null;
      state.reanalysisStatus = 'idle';
    },
    clearError: (state) => {
      state.error = null;
    },
    updateCurrentSessionFields: (state, action: PayloadAction<Record<string, string | string[]>>) => {
      if (state.currentSession) {
        state.currentSession.extracted_fields = {
          ...state.currentSession.extracted_fields,
          ...action.payload,
        };
      }
    },
    clearReanalysisStatus: (state) => {
      state.reanalysisStatus = 'idle';
    },
    setPolling: (state, action: PayloadAction<boolean>) => {
      state.isPolling = action.payload;
    },
    // Switching lists drops the merge picks: a session picked in one list may not be in the other.
    // Show Archived counts as a switch, since turning it off refetches without archived sessions.
    setViewAllSessions: (state, action: PayloadAction<boolean>) => {
      state.viewAllSessions = action.payload;
      state.mergeSelection.selectedIds = [];
    },
    setViewValidatedSessions: (state, action: PayloadAction<boolean>) => {
      state.viewValidatedSessions = action.payload;
      state.mergeSelection.selectedIds = [];
    },
    setIncludeArchivedSessions: (state, action: PayloadAction<boolean>) => {
      state.includeArchivedSessions = action.payload;
      state.mergeSelection.selectedIds = [];
    },
    // Turning merge mode on or off starts over: no picks, and every session shown.
    setMergeMode: (state, action: PayloadAction<boolean>) => {
      state.mergeSelection = { isMergeMode: action.payload, selectedIds: [], hideUnmergeable: false };
    },
    setHideUnmergeable: (state, action: PayloadAction<boolean>) => {
      state.mergeSelection.hideUnmergeable = action.payload;
    },
    // Picks a session, or unpicks it if already picked. A full selection ignores more picks.
    toggleMergeSelection: (state, action: PayloadAction<string>) => {
      const { selectedIds } = state.mergeSelection;
      if (selectedIds.includes(action.payload)) {
        state.mergeSelection.selectedIds = selectedIds.filter((id) => id !== action.payload);
      } else if (selectedIds.length < MERGE_SELECTION_SIZE) {
        selectedIds.push(action.payload);
      }
    },
    // A finished merge archives the picked sessions, so they can't stay picked.
    clearMergeSelection: (state) => {
      state.mergeSelection.selectedIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSessions.pending, (state) => {
        state.ownerSessionsStatus = 'pending';
        state.error = null;
      })
      .addCase(fetchSessions.fulfilled, (state, action) => {
        state.ownerSessionsStatus = 'fulfilled';
        state.ownerSessions = action.payload;
      })
      .addCase(fetchSessions.rejected, (state, action) => {
        state.ownerSessionsStatus = 'rejected';
        state.error = action.payload as string;
      });

    // ===== All sessions (admin view — separate from owner sessions) =====
    builder
      .addCase(fetchAllSessions.pending, (state, action) => {
        const isFirstPage = !action.meta.arg;
        if (isFirstPage) {
          state.allSessionsStatus = 'pending';
          state.allSessionsNextToken = null;
          state.error = null;
        }
        state.allSessionsLoading = true;
      })
      .addCase(fetchAllSessions.fulfilled, (state, action) => {
        const { sessions, next_token, isFirstPage } = action.payload;
        state.allSessionsStatus = 'fulfilled';
        state.allSessions = isFirstPage ? sessions : [...state.allSessions, ...sessions];
        state.allSessionsNextToken = next_token;
        state.allSessionsLoading = next_token !== null;
      })
      .addCase(fetchAllSessions.rejected, (state, action) => {
        state.allSessionsStatus = 'rejected';
        state.allSessionsLoading = false;
        state.error = action.payload as string;
      });

    // ===== Validated sessions (the auditor work list — spans all users and sites) =====
    builder
      .addCase(fetchValidatedSessions.pending, (state) => {
        state.validatedSessionsStatus = 'pending';
        state.error = null;
      })
      .addCase(fetchValidatedSessions.fulfilled, (state, action) => {
        state.validatedSessionsStatus = 'fulfilled';
        state.validatedSessions = action.payload;
      })
      .addCase(fetchValidatedSessions.rejected, (state, action) => {
        state.validatedSessionsStatus = 'rejected';
        state.error = action.payload as string;
      });

    // ===== Session detail =====
    builder
      .addCase(fetchSession.pending, (state) => {
        state.fetchSessionStatus = 'pending';
        state.error = null;
      })
      .addCase(fetchSession.fulfilled, (state, action) => {
        state.fetchSessionStatus = 'fulfilled';
        state.currentSession = action.payload;
        state.notes = action.payload.notes || [];

        // Update metadata in ownerSessions if present
        const ownerIdx = state.ownerSessions.findIndex(s => s.session_id === action.payload.session_id);
        if (ownerIdx !== -1) {
          state.ownerSessions[ownerIdx] = {
            ...state.ownerSessions[ownerIdx],
            metadata: action.payload.metadata,
            updated_at: action.payload.updated_at
          };
        }
      })
      .addCase(fetchSession.rejected, (state, action) => {
        state.fetchSessionStatus = 'rejected';
        state.error = action.payload as string;
      });

    // Update session fields
    builder
      .addCase(updateSessionFields.pending, (state) => {
        state.updateFieldsStatus = 'pending';
        state.error = null;
      })
      .addCase(updateSessionFields.fulfilled, (state, action) => {
        state.updateFieldsStatus = 'fulfilled';
        state.currentSession = action.payload;
      })
      .addCase(updateSessionFields.rejected, (state, action) => {
        state.updateFieldsStatus = 'rejected';
        state.error = action.payload as string;
      });

    // Validation fields (locks reviewed values + advances validation_state)
    builder
      .addCase(validateSessionFields.pending, (state) => {
        state.updateFieldsStatus = 'pending';
        state.error = null;
      })
      .addCase(validateSessionFields.fulfilled, (state, action) => {
        state.updateFieldsStatus = 'fulfilled';
        state.currentSession = action.payload;
      })
      .addCase(validateSessionFields.rejected, (state, action) => {
        state.updateFieldsStatus = 'rejected';
        state.error = action.payload as string;
      });

    // Audit results (records audited_fields + advances audit_state; touches no field store)
    builder
      .addCase(auditSessionFields.pending, (state) => {
        state.updateFieldsStatus = 'pending';
        state.error = null;
      })
      .addCase(auditSessionFields.fulfilled, (state, action) => {
        state.updateFieldsStatus = 'fulfilled';
        state.currentSession = action.payload;
      })
      .addCase(auditSessionFields.rejected, (state, action) => {
        state.updateFieldsStatus = 'rejected';
        state.error = action.payload as string;
      });

    // Generate summary
    builder
      .addCase(generateSummary.pending, (state) => {
        state.generateSummaryStatus = 'pending';
        state.error = null;
      })
      .addCase(generateSummary.fulfilled, (state, action) => {
        state.generateSummaryStatus = 'fulfilled';
        if (state.currentSession) {
          state.currentSession.summary_content = action.payload.summary;
          state.currentSession.summary_generated_at = action.payload.generated_at;
        }
      })
      .addCase(generateSummary.rejected, (state, action) => {
        state.generateSummaryStatus = 'rejected';
        state.error = action.payload as string;
      });

    // Save note (create or update)
    builder
      .addCase(saveNote.pending, (state) => {
        state.saveNoteStatus = 'pending';
        state.error = null;
      })
      .addCase(saveNote.fulfilled, (state, action) => {
        state.saveNoteStatus = 'fulfilled';
        const { note, isUpdate, reanalysisTriggered } = action.payload;

        if (isUpdate) {
          state.notes = state.notes.map(n =>
            n.note_id === note.note_id ? note : n
          );
        } else {
          state.notes.push(note);
        }

        if (reanalysisTriggered) {
          state.reanalysisStatus = 'pending';
        }
      })
      .addCase(saveNote.rejected, (state, action) => {
        state.saveNoteStatus = 'rejected';
        state.error = action.payload as string;
      });

    // Update metadata
    builder
      .addCase(updateSessionMetadata.pending, (state) => {
        state.updateMetadataStatus = 'pending';
        state.error = null;
      })
      .addCase(updateSessionMetadata.fulfilled, (state, action) => {
        state.updateMetadataStatus = 'fulfilled';
        if (state.currentSession) {
          state.currentSession.metadata = action.payload.metadata;
          state.currentSession.updated_at = action.payload.updated_at;
        }
        
        // Update in ownerSessions if present
        const ownerIdx = state.ownerSessions.findIndex(s => s.session_id === action.payload.session_id);
        if (ownerIdx !== -1) {
          state.ownerSessions[ownerIdx].metadata = action.payload.metadata;
          state.ownerSessions[ownerIdx].updated_at = action.payload.updated_at;
        }
      })
      .addCase(updateSessionMetadata.rejected, (state, action) => {
        state.updateMetadataStatus = 'rejected';
        state.error = action.payload as string;
      });

    // Delete session — remove from ownerSessions (safe), and also from allSessions for UI consistency
    builder
      .addCase(deleteSession.fulfilled, (state, action) => {
        state.ownerSessions = state.ownerSessions.filter(s => s.session_id !== action.payload);
        state.allSessions = state.allSessions.filter(s => s.session_id !== action.payload);
        // A deleted session can't stay picked for a merge.
        state.mergeSelection.selectedIds = state.mergeSelection.selectedIds.filter((id) => id !== action.payload);
        // Clear current session if it's the one being deleted
        if (state.currentSession?.session_id === action.payload) {
          state.currentSession = null;
          state.notes = [];
        }
      })
      .addCase(deleteSession.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Fetch session status (lightweight polling — updates currentSession status/updated_at)
    builder
      .addCase(fetchSessionStatusThunk.fulfilled, (state, action) => {
        if (state.currentSession && state.currentSession.session_id === action.payload.session_id) {
          state.currentSession.status = action.payload.status as SessionStatus;
          state.currentSession.running_tasks = action.payload.running_tasks ?? [];
          state.currentSession.updated_at = action.payload.updated_at;
        }
      });
    // No pending/rejected handlers — silent polling, errors are ignored

    // Update group_id (site)
    builder
      .addCase(updateSessionGroupId.fulfilled, (state, action) => {
        if (state.currentSession && state.currentSession.session_id === action.payload.session_id) {
          state.currentSession.group_id = action.payload.group_id;
          state.currentSession.updated_at = action.payload.updated_at;
        }
      });
  },
});

export const { clearCurrentSession, clearError, updateCurrentSessionFields, clearReanalysisStatus, setPolling, setViewAllSessions, setViewValidatedSessions, setIncludeArchivedSessions, setMergeMode, setHideUnmergeable, toggleMergeSelection, clearMergeSelection } = sessionSlice.actions;
export default sessionSlice.reducer;
